/* eslint-disable unicorn/prefer-query-selector */
/* eslint-disable unicorn/consistent-destructuring */
import $ from 'jquery';
import BaseEntity from './BaseEntity.js';
import { loadPartial } from '../../remote/partial-content.js';
import { getElementUrl, setElementUrl } from '../../util/custom.js';
import { whenElementInView } from '../../util/intersection/async.js';
import { handleRemoteResponse } from '../../remote/remote-response.js';
import MpHtmlEditor from '../../mpro/dynamic-plugins/widgets/html-editor/Widget.js';

export const selectors = {
	form: '> form[data-mp-dt-form]',
	table: '> table[data-mp-dt]',
	checkAll: 'thead:first input[data-mp-dt-check-all]',
	checkers: 'tbody:first input[data-mp-dt-checker]',
	batchActionsRow: '.actions-row[data-batch-actions-row]',
	batchActions: 'a[data-mp-dt-batch-action], button[data-mp-dt-batch-action]',
	clickRemoteActions: 'a[data-mp-dt-remote-action], button[data-mp-dt-remote-action], .on-click-target[data-mp-dt-remote-action]',
	changeRemoteActions: 'input[data-mp-dt-remote-action], select[data-mp-dt-remote-action], textarea[data-mp-dt-remote-action], .on-change-target[data-mp-dt-remote-action]',
	editInPlaceWrapper: 'div[data-mp-edit-in-place]',
	editInPlaceReader: 'div[data-mp-eip-reader]',
	editInPlaceWriter: 'div[data-mp-eip-writer]',
	editInPlaceCancel: 'a[data-mp-btn-eip-cancel], button[data-mp-btn-eip-cancel]',
	saveButtonsWrapper: 'label[data-save-buttons], div[data-save-buttons]',
	toggleUnfold: 'a[data-mp-dt-toggle-unfold], button[data-mp-dt-toggle-unfold]',
	toggleUnfoldDataProp: 'mpDtToggleUnfold',
	isUnfoldedDataProp: 'mpDtUnfolded',
	row: 'tr[data-mp-dt-row]',
	rowHandle: 'tr',
	subRow: 'tr[data-mp-dt-subrow]',
	groupHeaderRow: 'tr[data-mp-dt-group-header-row]',
	reloadRow: 'tr[data-mp-dt-reload-row]',
	reloadRowButton: 'a[data-mp-dt-reload-row], button[data-mp-dt-reload-row]',
	loadMoreRow: 'tr[data-mp-dt-load-more-row]',
	loadMoreButton: 'a[data-mp-dt-load-more], button[data-mp-dt-load-more]',
};

/**
 * @class Datatable
 */
export default class Datatable extends BaseEntity {

	static get NAME() {
		return 'Datatable';
	}

	/**
	 * Global datatable selectors
	 */
	static selectors = {
		dtContainer: '.mp-dt-container',
	};

	/**
	 * @param {JQuery=} $container
	 */
	static factoryInit($container) {

		// Init the datatable instances
		/** @type {JQuery<HTMLDivElement>} */ // @ts-ignore
		const $datatableContainer = $(this.selectors.dtContainer, $container);
		if ($datatableContainer.length > 0) {
			for (const el of $datatableContainer) {
				(new this(el)).initHandlers();
			}
		}
	}

	/** @type {HTMLDivElement} */
	dtContainer;
	/** @type {JQuery<HTMLDivElement>} */
	$dtContainer;

	selectors = selectors;

	actions = {

		// Use base actions
		// @ts-ignore: Property 'actions' is used before its initialization.ts(2729)
		// ? This is not a problem if we intend to use the actions AFTER the constructor's super() call
		...this.actions,
	};

	/** @type {JQuery<HTMLTableElement>} */
	$table;
	/** @type {JQuery<HTMLFormElement>} */
	$form;
	/** @type {JQuery<HTMLInputElement>} */
	$checkAll;
	/** @type {JQuery<HTMLTableCellElement>} */
	$checkerCell;
	/** @type {JQuery<HTMLTableRowElement>} */
	$checkerRow;
	/** @type {JQuery<HTMLTableCellElement>} */
	$checkerOverlayCell;
	/** @type {JQuery<HTMLTableRowElement>} */
	$checkerOverlayRow;
	/** @type {JQuery<HTMLTableCellElement>} */
	$overlayInfoCell;

	/** @type {number | NodeJS.Timeout} */
	_timerHandleDeferred;

	/** @type {boolean} */
	allowFullSelection = true;

	/**
	 * @param {HTMLDivElement} dtContainer
	 */
	constructor(dtContainer) {
		super();
		this.init(dtContainer);
	}

	/**
	 * @param {HTMLDivElement} dtContainer
	 */
	init(dtContainer) {
		this.dtContainer = dtContainer;
		this.$dtContainer = $(dtContainer);
		// ? The form is not always present, but is always a direct child of the container and is always the first child
		// @ts-ignore
		this.$form = this.$dtContainer.find(this.selectors.form).first();
		// ? The table is either a direct child of the container or a direct child of the form
		// @ts-ignore
		this.$table = this.$form.length > 0 ? this.$form.find(this.selectors.table).first() : this.$dtContainer.find(this.selectors.table).first();
		// ? The checkAll checkbox is always inside the table
		// @ts-ignore
		this.$checkAll = this.$table.find(this.selectors.checkAll);
		// @ts-ignore
		this.$checkerCell = this.$checkAll.closest('td, th');
		// @ts-ignore
		this.$checkerRow = this.$checkerCell.parent();

		this.initCheckerOverlay();

		// These handlers need to be called only once
		this.handleHoverRow();
		this.handleSortableRows();
	}

	/**
	 * Select all records handling
	 */
	initCheckerOverlay() {

		if (this.$table.data('mp-dt-allow-full-selection') === false) {
			this.allowFullSelection = false;
		}

		if (this.allowFullSelection === false) {
			return;
		}

		const checkerCellIndex = this.$checkerCell.index();
		// @ts-ignore
		const totalColSpan = this.$checkerRow.children().map((i, /** @type {HTMLTableCellElement} */v) => v.colSpan || 1).get().reduce((a, b) => a + b, 0);

		// style="position: absolute; top: 0; left: 0; right: 0; display: grid; grid-template-columns: auto 1fr;"
		this.$checkerOverlayRow = this.$checkerRow.clone().hide().css({ position: 'absolute', top: 0, left: 0, right: 0, display: 'grid', gridTemplateColumns: 'auto 1fr' });
		const $checkerOverlayCells = this.$checkerOverlayRow.children();

		// @ts-ignore
		this.$checkerOverlayCell = $checkerOverlayCells.filter(`:nth-child(${checkerCellIndex + 1})`).empty();
		$checkerOverlayCells.not(this.$checkerOverlayCell).remove();

		this.$overlayInfoCell = $(`<th scope="col" colspan="${totalColSpan - 1}"></th>`);
		this.$checkerOverlayCell.after(this.$overlayInfoCell);
		this.$checkerRow.after(this.$checkerOverlayRow);
		// Make sure the overlay row is initially hidden
		this.$checkerOverlayRow.hide();
	}

	/**
	 * Init datatable handlers
	 *
	 * ?Note: This function is bound to the instance, so it can be used as an event handler or passed as a callback
	 *
	 * @param {JQuery=} $container
	 */
	initHandlers = ($container) => {
		if (!$container) {
			$container = this.$dtContainer;
		}
		this.deferHeavyHandlers();
		this.handleBatchActions($container);
		this.handleRemoteActions($container);
		this.handleEditInPlace($container);
		this.handleSaveButtons($container);
		this.handleToggleUnfoldRows($container);
		this.handleReloadRowFragment($container);
		this.handleLoadMoreRows($container);
	};

	/**
	 * The handleCheckAll is very CPU intensive, we need to defer it to only one call per tick
	 */
	deferHeavyHandlers() {

		if (this._timerHandleDeferred) {
			clearTimeout(this._timerHandleDeferred);
			this._timerHandleDeferred = null;
		}

		this._timerHandleDeferred = setTimeout(() => {
			this._timerHandleDeferred = null;
			this.handleCheckAll();
		}, 100);
	}

	/**
	 * Datatable checker handling
	 */
	handleCheckAll() {

		/** @type {JQuery<HTMLInputElement>} */ // @ts-ignore
		const $allCheckers = this.$table.find(this.selectors.checkers);
		const $allEnabledCheckers = $allCheckers.not(':disabled');
		const totalEnabledCheckers = $allEnabledCheckers.length;
		// Get actions that are not already disabled
		// const $actionsRow = this.$form.find(this.selectors.batchActionsRow);
		// Get only the action buttons that have a disabled class that came from the backend (the __internal__ class is added here, not on the backed, in order to differentiate between the two)
		// const $actionButtons = $actionsRow.find(this.selectors.batchActions).not('.disabled:not(.__internal__)');
		// const checkerCell = this.$checkAll[0];
		const recset = this.$checkAll.data('mp-dt-recset') || {};
		const totalRecords = recset.total || totalEnabledCheckers;
		if (!this.$checkAll.data('mp-dt-recset')) {
			this.$checkAll.data('mp-dt-recset', recset);
		}
		recset.isFullSelection = false;
		recset.isFirstInit = true;
		const canDoFullSelection = (totalRecords > totalEnabledCheckers) && recset.filterValue ? true : false;

		this.$checkAll
			.off('change.dt-check-all')
			.on('change.dt-check-all', function(e) {
				$allEnabledCheckers.prop('checked', this.checked).trigger('change');
			});
		// .on('click.dt-check-all', (e) => {

		// 	// The check action already happened, we have to read the inverse value
		// 	if (!checkerCell.checked) {
		// 		return;
		// 	}

		// 	// If all records are already selected, do nothing
		// 	if (totalRecords <= totalEnabledCheckers) {
		// 		return;
		// 	}

		// 	const popover = new Popover(this, {
		// 		trigger: 'manual',
		// 		placement: 'bottom',
		// 		offset: [0, 12],
		// 		html: true,
		// 		template: '<div class="popover" role="tooltip">' +
		// 			'<div class="popover-arrow"></div>' +
		// 			'<h3 class="popover-header"></h3>' +
		// 			'<div class="popover-body p-1"></div>' +
		// 			'</div>',
		// 		content: $(`
		// 		<dl class="mb-0">
		// 			<dt><a class="dropdown-item" href="#">Select 20 items on this page</a></dt>
		// 			<dt><a class="dropdown-item" href="#">Select all 100 items</a></dt>
		// 			<dt><hr class="dropdown-divider"></dt>
		// 			<dt><a class="dropdown-item" href="#">Deselect all</a></dt>
		// 		</dl>
		// 	`)[0],
		// 	});
		// 	popover.show();

		// 	// If we prevent the default, the checkbox will not be checked (the checkbox will be unchecked in the next tick, but it will not be visible)
		// 	e.preventDefault();
		// });

		$allEnabledCheckers
			.off('change.dt-row-checker')
			.on('change.dt-row-checker', (e) => {

				const $checkedRows = $allEnabledCheckers.filter(':checked');
				const cntChecked = $checkedRows.length;
				const isDisabled = (cntChecked === 0);

				const checker = e.target;
				$(checker).closest(this.selectors.row).toggleClass('table-active', checker.checked);

				// Handle the overlay row
				if (this.allowFullSelection === true) {
					if (cntChecked === 0) {
						if (this.$checkAll.parent().is(this.$checkerCell) === false || recset.isFirstInit) {
							this.$checkAll.appendTo(this.$checkerCell);
							this.$checkerOverlayRow.hide();
							// this.$checkerRow.show();
							recset.isFullSelection = false;
						}
					}
					else {

						// Make sure the isFullSelection flag is set to false if the user unchecks a row
						if (cntChecked !== totalEnabledCheckers) {
							recset.isFullSelection = false;
						}

						if (this.$checkAll.parent().is(this.$checkerOverlayCell) === false || recset.isFirstInit) {
							this.$checkAll.appendTo(this.$checkerOverlayCell);
							// this.$checkerRow.hide();
							this.$checkerOverlayRow.show();

							const infoText = ['<span data-info-text class="fw-normal text-black-50__"></span>'];
							if (canDoFullSelection) {
								infoText.push(`<a href="javascript:void(0);" class="text-primary fw-normal text-decoration-none" data-select-all-records style="display: none;">${mpro.langvar('select_count_records', { count: totalRecords.toString() })}</a>`);
							}
							this.$overlayInfoCell.html(infoText.join(' '));
							this.$overlayInfoCell.find('a[data-select-all-records]').on('click', () => {
								recset.isFullSelection = true;
								this.$checkAll.prop('checked', true).trigger('change');
							});
						}

						if ((recset.isFullSelection !== true) && (cntChecked === totalEnabledCheckers)) {
							this.$overlayInfoCell.find('a[data-select-all-records]').show();
						}
						else {
							this.$overlayInfoCell.find('a[data-select-all-records]').hide();
						}

						// Update the recset.isFirstInit until the next loadPartial call
						recset.isFirstInit = false;

						// Update the info text
						this.$overlayInfoCell.find('span[data-info-text]').html(
							recset.isFullSelection
								? mpro.langvar('count_selected_records', { count: totalRecords.toString() })
								: `${mpro.langvar('count_selected_records', { count: cntChecked.toString() })}&nbsp;&nbsp;&nbsp;`,
						);

						// Hide the select all link if all records are already selected
						if (recset.isFullSelection === true) {
							this.$overlayInfoCell.find('a[data-select-all-records]').hide();
						}
					}
				}

				// If all checkers are checked, check the check all checkbox
				if (cntChecked === totalEnabledCheckers) {
					this.$checkAll.prop({
						checked: true,
						indeterminate: false,
					});
				}
				// If all checkers are unchecked, uncheck the check all checkbox
				else {
					this.$checkAll.prop({
						checked: false,
						indeterminate: !isDisabled,
					});
				}

				// $actionButtons.toggleClass('disabled __internal__', isDisabled);
			})
			.trigger('change.dt-row-checker');
	}

	/**
	 * Datatable row hover handling
	 */
	handleHoverRow() {

		const { $table } = this;

		// Only if table-hover class is present
		if ($table.hasClass('table-hover') === false) {
			return;
		}

		// Add delegate handler on the table
		$table.off('click.mp-dt-row-hover').on('click.mp-dt-row-hover', this.selectors.row, (e) => {

			// Ignore clicks on inputs, links and buttons
			if ($(e.target).is('input, select, textarea, a, button')) {
				return;
			}

			const $row = $(e.currentTarget);
			const $checker = $row.find('input[type="checkbox"]').first();
			const isChecked = $checker.prop('checked');

			$checker.prop('checked', !isChecked).trigger('change');
		});
	}

	/**
	 * Datatable batch action click handling
	 *
	 * @param {JQuery=} $container
	 */
	handleBatchActions($container) {

		// ? Always select the first element (there could be multiple tables inside one another (from subrows))
		const $cont = ($container || this.$dtContainer).first();

		/** @type {JQuery<HTMLAnchorElement & HTMLButtonElement>} */ // @ts-ignore
		const $batchActions = $cont.find(this.selectors.batchActions);

		// Nothing to do
		if ($batchActions.length === 0) {
			return;
		}

		for (const button of $batchActions) {

			const $batchActionButton = $(button);

			$batchActionButton.off('click.dt-batch-action').on('click.dt-batch-action', (e) => {

				// Not a valid action
				if (
					!(button.dataset.modalUrl || button.dataset.modal || button.dataset.drawerUrl || button.dataset.drawer || button.dataset.url || button.dataset.href) &&
					(!button.href || button.href.startsWith('#') || button.href.startsWith('javascript:'))
				) {
					return;
				}

				/** @type {JQuery<HTMLInputElement>} */ // @ts-ignore
				const $checkers = this.$table.find(this.selectors.checkers).not(':disabled').filter(':checked');

				// Nothing checked
				if ($checkers.length === 0) {
					e.preventDefault();
					e.stopPropagation();
					e.stopImmediatePropagation();

					mpro.toast('error', {
						content: mpro.langvar('hint_batch_action'),
					});

					return;
				}

				const originalUrl = getElementUrl(button);

				// console.log('originalUrl', originalUrl);

				const url = new URL(originalUrl.url, document.URL);
				const recset = this.$checkAll.data('mp-dt-recset') || {};

				// Full selection is enabled
				if (recset.isFullSelection === true && recset.filterName && recset.filterValue) {
					url.searchParams.append(recset.filterName, recset.filterValue);
				}
				// Full selection is disabled
				else {
					for (const checker of $checkers) {
						url.searchParams.append(checker.name, checker.value);
					}
				}

				// console.log('new elementUrl', url.href);

				// Save the new url
				setElementUrl(button, url.href);
			});
		}
	}

	/**
	 * Datatable row action click handling
	 *
	 * @param {JQuery=} $container
	 */
	handleRemoteActions($container) {

		// ? Always select the first element (there could be multiple tables inside one another (from subrows))
		const $cont = ($container || this.$dtContainer).first();

		const $clickActionElements = $cont.find(this.selectors.clickRemoteActions);
		const $changeActionElements = $cont.find(this.selectors.changeRemoteActions);

		// Nothing to do
		if ($clickActionElements.length === 0 && $changeActionElements.length === 0) {
			return;
		}

		const {
			row: rowSelector,
			subRow: subRowSelector,
		} = this.selectors;

		/**
		 * Attach a handler for datatable remote click actions
		 */
		$clickActionElements.off('click.dt-remote-action').on('click.dt-remote-action', async (e) => {

			/** @type {HTMLAnchorElement & HTMLButtonElement} */ // @ts-ignore
			// Use the element that triggered the event (might be a child of the actual element)
			const actionButton = e.target;
			const $actionButton = $(actionButton);

			const data = $(e.delegateTarget).data(); // or we can also use e.currentTarget, they are the same in this case
			const mpDtRemoteAction = data.mpDtRemoteAction || {};
			const url = mpDtRemoteAction.url || data.mpDtRemoteActionUrl || actionButton.href;

			// Not a valid action
			if (!url || url.startsWith('#') || url.startsWith('javascript:')) {
				return;
			}

			// Final event
			e.preventDefault();
			e.stopImmediatePropagation();
			e.stopPropagation();

			let $row = $actionButton.closest(rowSelector);

			// Add all subrows
			const $subRows = $row.nextAll(`${subRowSelector}[data-parent-key="${$row.data('key')}"]`);

			if ($subRows.length > 0) {
				$row = $row.add($subRows);
			}

			try {
				// Reload partial content from remote source
				const partial = await loadPartial($row, {
					...mpDtRemoteAction,
					url,
					type: 'fragment',
					replaceContainer: true, // will also allow initCallback to be called on the parent of the row, which is a wrapper div
					initCallback: this.initHandlers,
					relatedTarget: $actionButton,
				});

				// Handle extra response fields using the standard handler
				handleRemoteResponse(partial);

				// Flash the row
				mpro.showFocusAnimation($row, { type: partial.success ? 'success' : 'error' });
			}
			catch (/** @type {any} */e) {

				// Ignore in progress errors
				if (e?.message?.startsWith('LoadPartialInProgress')) {
					// Do nothing
				}
				else {
					// Flash the row
					mpro.showFocusAnimation($row, { type: 'error' });
				}
			}
		});

		/**
		 * Attach a handler for datatable remote change actions
		 */
		$changeActionElements.off('change.dt-remote-action').on('change.dt-remote-action', async (e, triggerData = {}) => {

			/** @type {HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement} */ // @ts-ignore
			// Use the element that triggered the event (might be a child of the actual element)
			const formElement = e.target;
			const $formElement = $(formElement);
			let allowRemoteAction;
			const {
				force = false,
				animationContainer = $formElement.next('.select2-container').length > 0 ? $formElement.next('.select2-container').find('.select2-selection') : $formElement,
			} = triggerData;

			// Forced remote action
			if (force === true) {
				allowRemoteAction = true;
			}
			// Check if the element is wrapped inside a save-buttons-wrapper
			else if ($formElement.closest(this.selectors.saveButtonsWrapper).length > 0) {
				allowRemoteAction = false;
			}
			else {
				allowRemoteAction = true;
			}

			// Go back
			if (allowRemoteAction === false) {
				return;
			}

			// Get remote-action config from the event target
			const data = $.extend({}, $(e.delegateTarget).data() || {}, $formElement.data() || {}); // or we can also use e.currentTarget, they are the same in this case
			const mpDtRemoteAction = data.mpDtRemoteAction || {};
			const url = mpDtRemoteAction.url || data.mpDtRemoteActionUrl;

			// Not a valid action
			if (!url || url.startsWith('#') || url.startsWith('javascript:')) {
				return;
			}

			// Final event
			e.preventDefault();
			e.stopImmediatePropagation();
			e.stopPropagation();

			// Save the current value of the element
			let oldValue;
			let isChecker;

			// Select
			if (formElement instanceof HTMLSelectElement) {
				oldValue = [...formElement.options].filter(option => option.defaultSelected).map(option => option.value);
			}
			// Checkers
			else if (formElement instanceof HTMLInputElement && ['checkbox', 'radio'].includes(formElement.type)) {
				oldValue = formElement.defaultChecked;
				isChecker = true;
			}
			// Input/Textarea
			else {
				oldValue = formElement.defaultValue;
			}

			// Serialize that data before mpro.addButtonLoading() - otherwise the field will be disabled
			let fieldData = $formElement.serialize();

			// Add empty value for checkboxes/radios that are not checked
			// @ts-ignore
			if (isChecker && !formElement.checked) {
				fieldData = encodeURIComponent(formElement.name) + '=';
			}

			// Special plugins treatment

			// const tiny = await $formElement.data('plugin-html-editor');
			/** @type {import('tinymce').Editor} */
			let tiny = MpHtmlEditor.getInstanceRef($formElement);
			if (MpHtmlEditor.isInstanceLoaded(tiny) === false) {
				tiny = null;
			}

			if (tiny) {
				tiny.setProgressState(true);
			}

			// Get the focused state
			const isElementFocused = document.activeElement === formElement;

			// Show a loading animation on the element
			// ? Note: disabled because too many animations
			// mpro.addButtonLoading(animationContainer);
			mpro.addButtonDisabled(animationContainer);

			// Disable the element
			if (animationContainer.is(formElement)) {
				formElement.disabled = true;
			}

			/**
			 * Action state
			 * @type {'success' | 'error'}
			 */
			let state;

			let $row = $formElement.closest(rowSelector);
			// Add all subrows
			const $subRows = $row.nextAll(`${subRowSelector}[data-parent-key="${$row.data('key')}"]`);
			if ($subRows.length > 0) {
				$row = $row.add($subRows);
			}

			try {
				// Reload partial content from remote source
				const partial = await loadPartial($row, {
					...mpDtRemoteAction,
					method: 'POST',
					url,
					data: fieldData,
					type: 'fragment',
					replaceContainer: true, // will also allow initCallback to be called on the parent of the row, which is a wrapper div
					initCallback: this.initHandlers,
					relatedTarget: $formElement,
					animation: false, // we are using the mpro.showFocusAnimation() below that has its own animation
				});

				// For now don't do anything with the response, except check for errors
				if (!partial.success) {

					console.warn('Remote action failed: %o', partial);

					// Handle the response in a generic way
					handleRemoteResponse(partial);

					throw new Error('Invalid response');
				}

				// Handle extra response fields using the standard handler
				handleRemoteResponse(partial);

				state = 'success';

				// The current row has been replaced
				// TODO: replace references to formElement
				if (partial.isContentful()) {
					// @ts-ignore
					$row = partial.$content;
				}

				// ----------------------------------------
				// Update the default value of the element
				// ----------------------------------------

				// Select
				if (formElement instanceof HTMLSelectElement) {
					if (formElement.multiple) {
						const selectedOptions = new Set(formElement.selectedOptions);
						for (const option of formElement.options) {
							option.defaultSelected = selectedOptions.has(option);
						}
					}
					else {
						for (const option of formElement.options) {
							option.defaultSelected = option.selected;
						}
					}
				}
				// Checkers
				else if (formElement instanceof HTMLInputElement && ['checkbox', 'radio'].includes(formElement.type)) {
					formElement.defaultChecked = formElement.checked;
				}
				// Input/Textarea
				else {
					formElement.defaultValue = formElement.value;
				}

				// Report success
				animationContainer.finish().addClass('action-success').animate({ fakeProp: 1 }, 2000, () => {
					animationContainer.removeClass('action-success');
				});
			}
			catch (/** @type {any} */ error) {

				// Ignore in progress errors
				if (error?.message?.startsWith('LoadPartialInProgress')) {
					// Do nothing
				}
				else {

					// Log the error details
					console.error(error);

					state = 'error';

					// ------------------------------------------------------------------------------
					// Restore the old value and trigger the function handler (not the change event)
					// ------------------------------------------------------------------------------

					// Select
					if (formElement instanceof HTMLSelectElement) {
						if (formElement.multiple) {
							for (const option of formElement.options) {
								// @ts-ignore
								option.selected = oldValue.includes(option.value);
							}
						}
						else {
							formElement.value = oldValue[0];
						}
					}
					// Checkers
					else if (formElement instanceof HTMLInputElement && ['checkbox', 'radio'].includes(formElement.type)) {
						// @ts-ignore
						formElement.checked = oldValue;
					}
					// Input/Textarea
					else {
						// @ts-ignore
						formElement.value = oldValue;
					}

					// Only trigger the change handler (if any)
					$formElement.triggerHandler('change');

					if (tiny) {
						// @ts-ignore
						tiny.setContent(oldValue);
						tiny.setDirty(false);
					}

					// Report error
					animationContainer.finish().addClass('action-error').animate({ fakeProp: 1 }, 3000, () => {
						animationContainer.removeClass('action-error');
					});
				}
			}
			finally {
				// Clear the loading animation
				// mpro.removeButtonLoading(animationContainer);
				mpro.removeButtonDisabled(animationContainer);

				// Enable the element
				if (animationContainer.is(formElement)) {
					formElement.disabled = false;
				}

				// Restore the focus
				if (isElementFocused && document.activeElement === document.body) {
					formElement.focus();
				}

				// Trigger a custom event
				$formElement.trigger(new $.Event('remote-action:done', { detail: { state } }), [state]);

				// Flash the row
				mpro.showFocusAnimation($row, { type: state });

				// Clear the editor loading state
				if (tiny) {
					tiny.setProgressState(false);
				}
			}
		});
	}

	/**
	 * Datatable save buttons handler
	 *
	 * @param {JQuery=} $container
	 */
	handleEditInPlace($container) {

		// ? Always select the first element (there could be multiple tables inside one another (from subrows))
		const $cont = ($container || this.$dtContainer).first();
		const { editInPlaceWrapper, editInPlaceReader, editInPlaceWriter, editInPlaceCancel } = this.selectors;
		const $eipContainers = $cont.find(editInPlaceWrapper);

		// Nothing to do
		if ($eipContainers.length === 0) {
			return;
		}

		for (const eipContainer of $eipContainers) {

			const $eipContainer = $(eipContainer);
			const $reader = $eipContainer.find(editInPlaceReader);
			const $writer = $eipContainer.find(editInPlaceWriter);
			const $cancel = $eipContainer.find(editInPlaceCancel);

			/** @type {HTMLInputElement | HTMLTextAreaElement} */ // @ts-ignore
			const readerInput = $reader.find('input, textarea')[0];
			/** @type {HTMLInputElement | HTMLTextAreaElement} */ // @ts-ignore
			const writerInput = $writer.find('input, textarea')[0];
			const $readerInput = $(readerInput);

			// Remove prev handlers first
			$eipContainer.off('.edit-in-place');
			$reader.off('.edit-in-place');
			$writer.off('.edit-in-place');
			$cancel.off('.edit-in-place');

			// Get the original style
			const writerStyle = $writer.attr('style');

			// eslint-disable-next-line unicorn/consistent-function-scoping
			const showReader = () => {
				$writer.attr('style', writerStyle);
				$reader.show();
				// Reset input value
				writerInput.value = writerInput.defaultValue;
				readerInput.value = writerInput.value;
			};

			// eslint-disable-next-line unicorn/consistent-function-scoping
			const showWriter = () => {
				$reader.hide();
				$writer.attr('style', '');
				// Get length right before clicking button
				const end = writerInput.value.length;
				writerInput.setSelectionRange(end, end);
				writerInput.focus();
			};
			$writer.on({
				'remote-action:done.edit-in-place': (e, state) => {
					// Report state (success/error)
					if (['success', 'error'].includes(state)) {
						const stateClass = state === 'success' ? 'action-success' : 'action-error';
						const duration = state === 'success' ? 2000 : 3000;
						$readerInput.finish().addClass(stateClass).animate({ fakeProp: 1 }, duration, () => {
							$readerInput.removeClass(stateClass);
						});
					}
					// Switch to reader mode
					showReader();
				},
				// @ts-ignore
				'keydown.edit-in-place': (e) => {
					if (e.key === 'Escape') {
						// Prevent the event from bubbling up (might be other listeners on the body that do different things)
						return false;
					}
				},
				// @ts-ignore
				'keyup.edit-in-place': (e) => {
					if (e.key === 'Escape') {
						// Switch to reader mode
						showReader();
						// Prevent the event from bubbling up (might be other listeners on the body that do different things)
						return false;
					}
				},
				'focusout.edit-in-place': () => {
					// Switch to reader mode
					if (writerInput.value === writerInput.defaultValue) {
						showReader();
					}
				},
			});
			$cancel.on('click.edit-in-place', showReader);
			$reader.on('click.edit-in-place', showWriter);
			// Listen for the custom 'reset' event
			$eipContainer.on('reset.edit-in-place', showReader);
		}
	}

	/**
	 * Datatable save buttons handler
	 *
	 * @param {JQuery=} $container
	 */
	handleSaveButtons($container) {

		// ? Always select the first element (there could be multiple tables inside one another (from subrows))
		const $cont = ($container || this.$dtContainer).first();
		const $wrappers = $cont.find(this.selectors.saveButtonsWrapper);

		// Nothing to do
		if ($wrappers.length === 0) {
			return;
		}

		for (const wrapper of $wrappers) {

			const $wrapper = $(wrapper);
			/** @type {JQuery<HTMLInputElement|HTMLTextAreaElement>} */ // @ts-ignore
			const $input = $wrapper.find('input, textarea');
			const $saveButton = $wrapper.find('[data-btn-action-save]');
			const $cancelButton = $wrapper.find('[data-btn-action-cancel]');
			const $allButtons = $saveButton.add($cancelButton);
			const isTextArea = $input.is('textarea');

			// Remove prev handlers first
			$input.off('.save-btn-action');
			$saveButton.off('.save-btn-action');
			$cancelButton.off('.save-btn-action');

			// Enable buttons
			$input.on('input.save-btn-action, change.save-btn-action', () => {
				$allButtons.prop('disabled', false);
			});

			// Enter key
			// @ts-ignore
			$input.on('keydown.save-btn-action', (e) => {
				if (e.key === 'Enter' && ((isTextArea === false) || (isTextArea && e.shiftKey))) {
					$saveButton.trigger('click');
					return false;
				}
			});

			// Custom event
			$input.on('remote-action:done.save-btn-action', () => {
				$allButtons.prop('disabled', true);
			});

			// Save click
			$saveButton.on('click.save-btn-action', (e) => {
				$input.trigger('change', [{ force: true, animationContainer: $wrapper }]);
				$allButtons.prop('disabled', true);
			});

			// Cancel click
			$cancelButton.on('click.save-btn-action', () => {
				// The 'reset' event is custom
				$input.val($input[0].defaultValue).trigger('reset');
				$allButtons.prop('disabled', true);
			});
		}
	}

	/**
	 * Datatable load more rows action
	 *
	 * @param {JQuery=} $container
	 */
	handleLoadMoreRows($container) {

		// ? Always select the first element (there could be multiple tables inside one another (from subrows))
		const $cont = ($container || this.$dtContainer).first();
		const $loadMoreRows = $cont.is(this.selectors.loadMoreRow) ? $cont : $cont.find(this.selectors.loadMoreRow);

		// Nothing to do
		if ($loadMoreRows.length === 0) {
			return;
		}

		for (const row of $loadMoreRows) {

			// Get the row container
			const $row = $(row);
			// Get all triggers
			const $triggers = $row.find(this.selectors.loadMoreButton);
			// Filter click triggers
			const $clickTriggers = $triggers.filter('a, button');
			// Rest of the triggers are dynamic (will trigger manually/programmatically)
			const $dynamicTriggers = $triggers.not($clickTriggers);
			// Get triggers that have auto-click when in viewport
			const $triggersInViewport = $triggers.filter((_, elem) => $(elem).data('mp-dt-load-more')?.mode === 'auto');

			// Trigger on click
			$clickTriggers.off('click.loadFragment').on('click.loadFragment', this._loadMoreRowsFragmentHandler);
			// Trigger on custom event
			$dynamicTriggers.off('mp-load-fragment.loadFragment').on('mp-load-fragment.loadFragment', this._loadMoreRowsFragmentHandler);

			// debugger;

			// Handle auto-viewport triggers
			for (const trigger of $triggersInViewport) {

				const $trigger = $(trigger);
				const data = $trigger.data('mp-dt-load-more');
				const offset = ('offset' in data) ? Number(data.offset) : 200;
				const $overlayBody = this.$dtContainer.closest('div[data-mp-overlay-body]');
				let root;

				// Use the overlay body as the scroll container
				if ($overlayBody.length > 0) {
					root = $overlayBody[0];
				}

				// @ts-ignore
				whenElementInView(trigger, {
					root,
					rootMargin: `0px 0px ${offset}px 0px`,
				}).then(() => {
					// Trigger click
					if ((trigger instanceof HTMLAnchorElement) || (trigger instanceof HTMLButtonElement)) {
						trigger.click();
					}
					// Trigger send custom event
					else {
						$trigger.triggerNative('mp-load-fragment');
					}
				});
			}
		}
	}

	/**
	 * Handler is assigned to multiple events
	 *
	 * @param {JQuery.TriggeredEvent} e
	 */
	_loadMoreRowsFragmentHandler = async (e) => {

		// Disable other actions
		e.preventDefault();
		e.stopPropagation();
		e.stopImmediatePropagation();

		// TODO: maybe add second argument to send the e.currentTarget as the container: mpro.loadFragment(e.target, e.currentTarget);

		try {
			// Get group header before adding new content
			const $prevGroupHeaders = this.$table.find('> tbody').children(this.selectors.groupHeaderRow);
			const $trigger = $(e.currentTarget);
			const data = $trigger.data('mp-dt-load-more') || {};
			const partial = await mpro.loadFragment(e.currentTarget, {
				...data,
				// container: this.$table.find('> tbody'),
				// buttonContainer: $trigger.closest(this.selectors.loadMoreRow),
				container: $trigger.closest(this.selectors.loadMoreRow),
				selector: [this.selectors.row, this.selectors.subRow, this.selectors.groupHeaderRow, this.selectors.loadMoreRow].join(','),
				replace: true,
				initCallback: this.initHandlers,
			});

			// Grouping is enabled, make sure results are grouped to their respective groups
			if ($prevGroupHeaders.length > 0) {

				const $responseContent = partial.$content;

				for (const prevGroupHeader of $prevGroupHeaders) {
					const $prevGroupHeader = $(prevGroupHeader);
					const groupId = prevGroupHeader.dataset.mpDtGroupHeaderRow;

					// Get all rows from the response that belong to the group
					const $newGroupHeader = $responseContent.filter(`[data-mp-dt-group-header-row="${groupId}"]`);

					if ($newGroupHeader.length === 0) {
						continue;
					}

					// Get the children of the group from the response
					const $newGroupChildren = $newGroupHeader.nextUntil('[data-mp-dt-group-header-row]').not(this.selectors.loadMoreRow);

					// Append the children to the original group
					$prevGroupHeader.nextUntil('[data-mp-dt-group-header-row]').last().after($newGroupChildren);

					// Remove the group header from the response
					$newGroupHeader.remove();
				}

				// Keep only HtmlElement types (not text nodes)
				mpro.showFocusAnimation($responseContent.filter('*'));
			}
		}
		catch (error) {
			console.warn(error);
		}
	};

	/**
	 * Datatable load more rows action
	 *
	 * @param {JQuery=} $container
	 */
	handleReloadRowFragment($container) {

		// ? Always select the first element (there could be multiple tables inside one another (from subrows))
		const $cont = ($container || this.$dtContainer).first();
		const $reloadRows = $cont.is(this.selectors.reloadRow) ? $cont : $cont.find(this.selectors.reloadRow);

		// Nothing to do
		if ($reloadRows.length === 0) {
			return;
		}

		for (const row of $reloadRows) {

			// Get the row container
			const $row = $(row);
			// Get all triggers
			const $triggers = $row.find(this.selectors.reloadRowButton);
			// Filter click triggers
			const $clickTriggers = $triggers.filter('a, button');
			// Rest of the triggers are dynamic (will trigger manually/programmatically)
			const $dynamicTriggers = $triggers.not($clickTriggers).add($row);
			// Get triggers that have auto-click when in viewport
			// TODO: find a way to not specify the data selector hardcoded
			const $triggersInViewport = $triggers.filter((_, elem) => $(elem).data('mp-dt-reload-row')?.mode === 'auto');

			// Trigger on click
			$clickTriggers.off('click.loadFragmentRow').on('click.loadFragmentRow', this._reloadRowFragmentHandler);
			// Trigger on custom event
			$dynamicTriggers.off('mp-load-fragment.loadFragmentRow').on('mp-load-fragment.loadFragmentRow', this._reloadRowFragmentHandler);

			// Handle auto-viewport triggers
			for (const trigger of $triggersInViewport) {

				const $trigger = $(trigger);
				// TODO: find a way to not specify the data selector hardcoded
				const data = $trigger.data('mp-dt-reload-row');
				const offset = ('offset' in data) ? Number(data.offset) : 200;
				const $overlayBody = this.$dtContainer.closest('div[data-mp-overlay-body]');
				let root;

				// Use the overlay body as the scroll container
				if ($overlayBody.length > 0) {
					root = $overlayBody[0];
				}

				// @ts-ignore
				whenElementInView(trigger, {
					root,
					rootMargin: `0px 0px ${offset}px 0px`,
				}).then(() => {
					// Trigger click
					if ((trigger instanceof HTMLAnchorElement) || (trigger instanceof HTMLButtonElement)) {
						trigger.click();
					}
					// Trigger send custom event
					else {
						$trigger.triggerNative('mp-load-fragment');
					}
				});
			}
		}
	}

	/**
	 * Handler is assigned to multiple events
	 *
	 * @param {JQuery.TriggeredEvent} e
	 */
	_reloadRowFragmentHandler = async (e) => {

		// Disable other actions
		e.preventDefault();
		e.stopPropagation();
		e.stopImmediatePropagation();

		try {
			// Get group header before adding new content
			const $trigger = $(e.currentTarget);
			const $row = $trigger.is(this.selectors.row) ? $trigger : $trigger.closest(this.selectors.row);
			const rowKey = $row.data('key');
			const data = {
				url: this.$table.data('mp-dt').reloadRowUrl,
				data: {
					id: rowKey,
				},
				...$row.data('mp-dt-reload-row'),
				...$trigger.data('mp-dt-reload-row'),
			};
			const partial = await mpro.loadFragment(e.currentTarget, {
				...data,
				// Select all subrows for the current row
				container: $row.add($row.siblings(this.selectors.subRow).filter(`[data-parent-key="${rowKey}"]`)),
				replace: true,
				initCallback: this.initHandlers,
			});

			if (partial.$content.length > 0) {
				// Keep only HtmlElement types (not text nodes)
				mpro.showFocusAnimation(partial.$content.filter('*'));
			}
		}
		catch (error) {
			console.warn(error);
		}
	};

	/**
	 * Datatable row unfold action
	 *
	 * @param {JQuery=} $container
	 */
	async handleToggleUnfoldRows($container) {

		// ? Always select the first element (there could be multiple tables inside one another (from subrows))
		const $cont = ($container || this.$dtContainer).first();
		const $triggers = $cont.is(this.selectors.toggleUnfold) ? $cont : $cont.find(this.selectors.toggleUnfold);

		// Nothing to do
		if ($triggers.length === 0) {
			return;
		}

		// Import the SortableRows class
		const { 'default': UnfoldableRow } = await import('./datatable-handlers/UnfoldableRow.js');

		for (const trigger of $triggers) {
			try {
				(new UnfoldableRow($(trigger), this.selectors, this.initHandlers)).init();
			}
			catch (error) {
				console.warn('UnfoldableRow error: %o', error);
			}
		}
	}

	/**
	 * Datatable sortable rows handling
	 */
	async handleSortableRows() {

		const { $table } = this;

		// Only if table-sortable class is present
		if ($table.is('[data-mp-dt-sortable]') === false) {
			return;
		}

		// Import the SortableRows class
		const { 'default': SortableRows } = await import('./datatable-handlers/SortableRows.js');
		try {
			(new SortableRows($table, this.selectors, this.initHandlers)).init();
		}
		catch (error) {
			console.warn('SortableRows error: %o', error);
		}
	}
}